import type { Language } from '@seek/melways-sites';

import { markApplicationsRead } from 'src/graphql/hirer/markApplicationsRead';
import { sendApplicationEmail } from 'src/graphql/hirer/sendApplicationEmail';
import { sendBulkApplicationEmail } from 'src/graphql/hirer/sendBulkApplicationEmail';
import type { PageConfig } from 'src/libs/PageConfig';

import { getDashisedBucket } from '../../../constants/jobApplicationStatus';
import {
  trackSentEmailMultiple,
  trackSentEmailSingle,
} from '../../../utils/events';
import type { IJobApplication } from '../JobApplication';
import type { IJobApplicationSummary } from '../JobApplicationSummary';
import type { IJobApplicationStore } from '../index';

export const sendEmailForSingleApplication = async (
  prospectId: string,
  subject: string,
  message: string,
  enableReplies: boolean,
  trackingAttributes: { candidateId: string },
) => {
  const sendEmail = await sendApplicationEmail({
    prospectId,
    subject,
    message,
    enableReplies,
  });
  trackSentEmailSingle({
    ...trackingAttributes,
    repliesEnabled: enableReplies,
  });
  return sendEmail;
};

export const updateApplicationsRead = async (
  jobApplicationStore: IJobApplicationStore,
  jobApplications: IJobApplicationSummary[],
  language: Language,
  displayCountry: string,
  addCustomError: PageConfig['dataDogRum']['addCustomError'],
) => {
  const unreadApplications = jobApplications.filter(
    (jobApp: IJobApplicationSummary) => jobApp.isUnread,
  );
  if (unreadApplications.length > 0) {
    const markApplicationsReadResult = await markApplicationsRead(
      unreadApplications.map((app: IJobApplicationSummary) => app.id),
      addCustomError,
    );
    if (markApplicationsReadResult) {
      unreadApplications.forEach((jobApp: IJobApplicationSummary) =>
        jobApp.setRead(),
      );
    }
  }
  await jobApplicationStore.bucketStatsStore.loadBucketStats(
    jobApplicationStore.rootStore.jobId,
    language,
    displayCountry,
    addCustomError,
  );
};

export const sendEmailForSelectedApplications = async (
  jobApplicationStore: IJobApplicationStore,
  subject: string,
  message: string,
  enableReplies: boolean,
  candidateCardStatus: 'Expand' | 'Collapse' | 'No RR',
  language: Language,
  displayCountry: string,
  addCustomError: PageConfig['dataDogRum']['addCustomError'],
) => {
  trackSentEmailMultiple({
    ...jobApplicationStore.bulkActionEventAttributes,
    repliesEnabled: enableReplies,
    bucket: getDashisedBucket(jobApplicationStore.bucket),
    candidateCardStatus,
  });
  const {
    selectedJobApplications,
    selectedJobApplicationIds: prospectIds,
    selectedJobApplicationsWithDetails,
  } = jobApplicationStore;

  const response = await sendBulkApplicationEmail({
    prospectIds,
    subject,
    message,
    enableReplies,
  });
  if (response?.success) {
    selectedJobApplicationsWithDetails.forEach((item: IJobApplication) => {
      item.prospectEmailStore.increaseEmailCount();
    });
    await updateApplicationsRead(
      jobApplicationStore,
      selectedJobApplications,
      language,
      displayCountry,
      addCustomError,
    );
    return true;
  }
  return false;
};
